import { IconButton, InputAdornment, TextField } from "@mui/material";
import { createTheme } from "@mui/material/styles";
import { ThemeProvider } from "@mui/system";
import React, { useState } from "react";
import frameworkIcons from "./assets/icons/frameworksIcons";
const theme = createTheme({
  size: {
    small: {
      height: 27,
      labelTop: -5,
    },
    medium: {
      height: 32,
      labelTop: -10,
    },
    auto: {
      labelTop: -10,
      height: "auto",
    },
  },
  components: {
    MuiTextField: {
      styleOverrides: {
        root: ({ ownerState, theme }) => {
          return {
            backgroundColor: "transparent !important",
            border: ownerState.error
              ? "none !important"
              : ownerState.variant === "outlined" &&
                `1px solid ${
                  ownerState.disabled ? "rgba(0, 0, 0, 0.25)" : "black"
                } !important`,
            borderBottom:
              ownerState.variant === "standard" && "1px solid black !important",
            borderRadius: ownerState.variant === "outlined" && 4,
            "& .MuiInputLabel-root": {
              color: ownerState.disabled ? "rgba(0,0,0,0.25)" : "black",
              top: theme.size[ownerState.size].labelTop,
              "&.MuiInputLabel-shrink": {
                overflow: "visible",
                background: "rgba(255, 255, 255, 1)",
                fontWeight: 700, //QA feedback
                lineHeight: "1em",
                letterSpacing: "0.06em",
                fontSize: 15,
                top: 0,
                padding: "0 5px",
                left: -5,
                "&.Mui-error": {
                  color: "#d32f2f",
                },
              },
            },
            "& .MuiOutlinedInput-root": {
              "::-webkit-scrollbar": {
                display: "none",
              },
              height: theme.size[ownerState.size].height,
              maxHeight: ownerState.size === "auto" ? 200 : "none",
              overflowX: "hidden",
              overflowY: "scroll",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
              background: ownerState.InputProps.readOnly ? "#a0a0a0" : "white",
              // background: "white",
              border: "none",
              "& .MuiOutlinedInput-notchedOutline": {
                // border: "1px solid black",
                border: "none",
              },
              "&.Mui-error .MuiOutlinedInput-notchedOutline": {
                border: "1px solid #d32f2f",
              },
              "&.Mui-focused": {
                background: "#fdeba4",
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
                "&.Mui-error .MuiOutlinedInput-notchedOutline": {
                  border: "1px solid #d32f2f",
                },
              },
            },
          };
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        root: {
          padding: "0 0 0 5px",
          "::after": {
            border: "none !important",
          },
          "::before": {
            border: "none !important",
          },
          "&.Mui-focused": {
            // background: "#fdeba4",
          },
          "&.Mui-error": {
            borderBottom: "1px solid #d32f2f",
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          padding: "0 0 0 5px",
        },
        input: {
          padding: "0 4px", //QA feedback
          "::placeholder": {
            color: "#020002", //QA feedback
            fontSize: 15, //QA feedback
            fontWeight: 700, //QA feedback
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          padding: 0, //QA feedback
          color: "#020202",
          fontWeight: "bold",
          "&.Mui-focused": {
            color: "black",
          },
          fontSize: 14,
          "& .MuiInputLabel-asterisk": {
            color: "red",
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          margin: 0,
          lineHeight: 1,
          fontWeight: 700,
          height: 13,
          width: "100%",
          padding: "0 14px",
          overflow: "hidden",
        },
      },
    },
  },
});
const TextInput = React.forwardRef((props, ref) => {
  const {
    autoComplete = "off",
    size = "medium",
    fullWidth = true,
    disabled = false,
    id,
    name,
    label,
    placeholder,
    value,
    type = "text",
    variant = "outlined",
    sx = {},
    helperText = "", //default value will be space (" ")
    error = false,
    inputProps = {},
    InputProps = {},
    InputLabelProps = {},
    onFocus = () => {},
    onBlur = () => {},
    onChange = () => {},
    onClick = () => {},
    onKeyDown = () => {}, //ONLY USED FOR DATEINPUT
    required,
    autoFocus,
    enableMaxLengthCheck = true,
    maxInputLength = 255,
  } = props;
  const [isFocused, setIsFocused] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  return (
    <ThemeProvider theme={theme}>
      <TextField
        fullWidth={fullWidth}
        disabled={disabled}
        inputRef={ref}
        id={id}
        name={name}
        label={label}
        variant={variant}
        size={size}
        value={value}
        inputProps={{ ...inputProps }}
        InputProps={{
          endAdornment:
            type === "password" ? (
              <InputAdornment position="end" style={{ marginRight: 20 }}>
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() => setShowPassword((prev) => !prev)}
                  edge="end"
                >
                  {
                    <img
                      src={
                        showPassword
                          ? frameworkIcons.viewPassword
                          : frameworkIcons.hidePassword
                      }
                      alt="password"
                      width={27}
                      height={27}
                    />
                  }
                </IconButton>
              </InputAdornment>
            ) : null,
          ...InputProps,
        }}
        InputLabelProps={{
          ...InputLabelProps,
          shrink: !!value || !!ref?.current?.value || isFocused,
        }}
        autoComplete={autoComplete}
        placeholder={placeholder}
        type={type === "password" && showPassword ? "text" : type}
        sx={sx}
        onFocus={(e) => {
          e.target.select();
          setIsFocused(true);
          onFocus(e);
        }}
        onChange={(e) => {
          if (enableMaxLengthCheck && e.target.value.length > maxInputLength)
            return;
          onChange(e);
        }}
        onBlur={(e) => {
          setIsFocused(false);
          onBlur(e);
        }}
        onClick={onClick}
        onKeyDown={onKeyDown}
        error={error}
        helperText={helperText}
        required={required}
        autoFocus={autoFocus}
        // multiline={true}
      />
    </ThemeProvider>
  );
});

export default TextInput;
